import { IntegrationType } from '@business/entities/integration';
import { OrgId, OrgResponse, PaymentId, PaymentMethodResponse, PaymentMethodType } from '@business/entities/organization';
import { CompactProjectResponse, ProjectId } from '@business/entities/project';
import { CommonService } from '@business/services';
import { Button } from '@core/components/Button';
import { FlashMessage } from '@core/components/FlashMessage';
import { FormSelect } from '@core/components/form';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { useHistory } from '@core/router';
import { formControl } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { R } from '@core/utils/r';
import { tapError } from '@core/utils/rxjs/operators';
import { openSelectProjectTypeModal } from '@modules/board/SelectProjectTypeModal';
import { showAlert, showConfirmation } from '@modules/common';
import { openSelectBoardModal } from '@modules/common/SelectBoardModal';
import { openSelectContactEmailModal } from '@modules/common/SelectContactEmailModal';
import { openProjectOverlay } from '@modules/projects/ProjectOverlay';
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { Card, CardBody, CardTitle, Col, DropdownMenu, DropdownToggle, Row, Spinner, UncontrolledDropdown } from 'reactstrap';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { OrganizationPageBloc } from '../../OrganizationPageBloc';
import { InvoiceLineTooltip } from './InvoiceLineTooltip';
import { openManualPaymentModal, openProcessPaymentModal } from './ProcessPaymentModal';
import { openSendInvoiceModal } from './SendInvoiceModal';
import { openEditProjectModal } from '@modules/projects/ProjectOverlay/EditProjectModal';
import { useLoadingState } from '@core/utils/repository/loading_state';

export interface OrgProjectsProps {
  orgId: OrgId;
  organization: OrgResponse;
  projects: CompactProjectResponse[];
  methods: PaymentMethodResponse[];
  bloc: OrganizationPageBloc;
  canProcess: boolean;
  isModal?: boolean;
}

export const OrgProjects = memo((props: OrgProjectsProps) => {
  const { orgId, projects, bloc, canProcess, organization, isModal } = props;
  const [isSendingInvoice, setSendingInvoice] = useState(false);
  const [active, setActive] = useState(true);
  const control = useMemo(() => formControl<number>({ initialValue: 0 }), []);
  const fullLoading = useLoadingState(bloc.fullLoading);

  const activeItems = CommonService.selectItems(
    ['Active', 'Canceled'],
    i => i,
    (_, index) => index,
  );

  const history = useHistory();
  const settings = useSettings();

  const isQBEnabled = useMemo(() => {
    if (settings) {
      return settings.integrations.filter(i => i.type === IntegrationType.quickbooks).length > 0;
    }
    return false;
  }, [settings]);

  const contactEmails = useMemo(
    () =>
      CommonService.selectItems(
        R.flatMap(organization.contacts, c => R.map(c.emails, e => ({ value: e.value, name: CommonService.getFullName(c) }))),
        n => `${n.name} <${n.value}>`,
        'value',
        true,
      ),
    [organization.contacts],
  );

  const onSendInvoice = useCallback(
    (id: ProjectId, emails: string[]) => {
      setSendingInvoice(true);
      bloc
        .sendInvoice(id, emails)
        .pipe(
          tap(() => setSendingInvoice(false)),
          tapError(() => setSendingInvoice(false)),
        )
        .subscribe();
    },
    [bloc],
  );

  const onProcessPayment = useCallback(
    (id: ProjectId, paymentId: PaymentId, amount: number, type: PaymentMethodType) => {
      return of(null).pipe(
        switchMap(() => {
          if (type === PaymentMethodType.ach) {
            return showAlert('Bank transfer', 'Bank transfer might take upto 3 days to complete, until then this payment will show as pending.');
          }
          return of(null);
        }),
        switchMap(() => {
          return bloc.processPayment(orgId, id, paymentId, amount);
        }),
      );
    },
    [bloc, orgId],
  );

  const onManualPayment = useCallback(
    (id: ProjectId, paymentId: PaymentId, amount: number, paymentDate: number) => {
      return bloc.manualPayment(orgId, id, paymentId, amount, paymentDate).pipe();
    },
    [bloc, orgId],
  );

  const onProjectUpdated = useCallback(() => {
    bloc.onReload();
  }, [bloc]);

  const columns: GCColumn<CompactProjectResponse>[] = useMemo(
    () => [
      {
        id: 'name',
        name: `${gcLabel('Project')} Title`,
        sortField: 'name',
        minWidth: '150px',
        sortable: true,
        cell: row => (
          <a className="ml-0" onClick={() => openProjectOverlay({ projectId: row.id, projectTitle: row.title })}>
            <InvoiceLineTooltip lineItems={row.lineItems} title={row.title} />
          </a>
        ),
      },
      {
        name: `${gcLabel('Project')} Total`,
        sortField: 'total',
        cell: item => Formatter.formatCurrency(item.total, item.currency),
        minWidth: '80px',
      },
      {
        name: 'Balance',
        sortField: 'balance',
        cell: item => Formatter.formatCurrency(item.balance, item.currency),
        minWidth: '80px',
      },
      {
        name: 'Date',
        sortField: 'date',
        cell: item => Formatter.formatTime(item.created),
        minWidth: '80px',
      },
      {
        omit: active,
        name: 'Canceled on',
        sortField: 'canceledAt',
        cell: item => Formatter.formatTime(item.canceledAt),
        minWidth: '80px',
      },
      {
        omit: !active,
        name: 'Action',
        id: 'action',
        width: '80px',
        cell: row => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="font-size-16 d-flex justify-content-center align-items-center text-dark">
                <i className="bx bx-dots-horizontal-rounded"></i>
              </DropdownToggle>

              <DropdownMenu container="body" className="dd-menu_outside">
                {row.boardId == '0' && (
                  <Fragment>
                    <a
                      className={`dropdown-item `}
                      onClick={() =>
                        openSelectBoardModal({
                          withStage: true,
                          title: `Add ${gcLabel('Project')} to Board`,
                          onSelect: (board, stage) => bloc.updateBoard(row.id, { board, stage }),
                        })
                      }
                    >
                      Add to Board
                    </a>
                    <div className="dropdown-divider"></div>
                  </Fragment>
                )}
                <a
                  className={`dropdown-item`}
                  onClick={() => {
                    if (isModal) {
                      return (window.location.href = `/projects/edit/${orgId}/${row.id}`);
                    }
                    if (row.invoice) {
                      history.push(`/projects/edit/${orgId}/${row.id}`);
                    } else {
                      openEditProjectModal({ projectId: row.id, onDone: onProjectUpdated });
                    }
                  }}
                >
                  Edit Project
                </a>
                <a
                  className={`dropdown-item ${isSendingInvoice && 'disabled'}`}
                  onClick={
                    () =>
                      openSendInvoiceModal({
                        emails: contactEmails,
                        projectId: row.id,
                        balance: row.balance,
                        default: R.split(row.billingEmail, ',').map(item => R.trim(item)),
                      })
                    // openSelectContactEmailModal({
                    //   title: 'Send Invoice',
                    //   emails: contactEmails,
                    //   onSelect: (emails: string[]) => onSendInvoice(row.id, emails),
                    //   default: R.split(row.billingEmail, ',').map(item => R.trim(item)),
                    //   button: 'Send',
                    // })
                  }
                >
                  Send Invoice
                  {isSendingInvoice && <Spinner className="ml-2" size="sm" color="primary" />}
                </a>
                {row.balance > 0 && (
                  <Fragment>
                    <a
                      className={`dropdown-item ${canProcess ? '' : 'disabled'}`}
                      onClick={() =>
                        openProcessPaymentModal({
                          max: row.balance,
                          methods: props.methods,
                          onSubmit: (paymentId, amount, type) => onProcessPayment(row.id, paymentId, amount, type),
                          currency: organization.currency,
                        })
                      }
                    >
                      Charge Payment Method
                    </a>
                  </Fragment>
                )}
                <a
                  className={`dropdown-item`}
                  onClick={() => {
                    CommonService.copyToClipboard(row.invoiceLink);
                    FlashMessage.success('Link Copied', 'Payment link has been copied to your clipboard');
                  }}
                >
                  Copy Payment Link
                </a>
                {row.balance > 0 && !isQBEnabled && (
                  <a
                    className={`dropdown-item ${canProcess ? '' : 'disabled'}`}
                    onClick={() =>
                      openManualPaymentModal({
                        max: row.balance,
                        onSubmit: (paymentId, amount, paymentDate) => onManualPayment(row.id, paymentId, amount, paymentDate),
                      })
                    }
                  >
                    Manual Payment
                  </a>
                )}
                {!row.onBoard && (
                  <a
                    className={`dropdown-item`}
                    onClick={() =>
                      showConfirmation('Add to Board', 'Are you sure you want to add this project to a board?')
                        .pipe(switchMap(() => bloc.showOnBoard(row.id)))
                        .subscribe()
                    }
                  >
                    Show on {gcLabel('Project')} Board
                  </a>
                )}
                <div className="dropdown-divider"></div>
                <a
                  className="dropdown-item text-danger"
                  onClick={() =>
                    bloc
                      .deleteProject(row.id)
                      .pipe(tap(() => bloc.onReload()))
                      .subscribe()
                  }
                >
                  Cancel {gcLabel('Project')}
                </a>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      active,
      isSendingInvoice,
      canProcess,
      isQBEnabled,
      bloc,
      isModal,
      orgId,
      history,
      onProjectUpdated,
      contactEmails,
      props.methods,
      organization.currency,
      onProcessPayment,
      onManualPayment,
    ],
  );

  return (
    <Card>
      <CardBody>
        <CardTitle className="d-flex align-items-center justify-content-between py-2">
          <div className="d-flex align-items-center">
            {gcLabel('Projects')}
            {fullLoading.loading && <Spinner className="ml-2 font-size-12" size="sm" color="primary" />}
          </div>
          <div className="d-flex">
            <div style={{ width: 200 }}>
              <FormSelect control={control} items={activeItems} onChange={val => setActive(!val)} />
            </div>
            <div style={{ width: 105, marginLeft: 10 }}>
              <Button intent="primary" onClick={() => openSelectProjectTypeModal({ orgId: orgId })}>
                Add {gcLabel('Project')}
              </Button>
            </div>
          </div>
        </CardTitle>

        {!fullLoading.loading && <GCTable columns={columns} data={R.filter(projects, p => p.isCanceled === !active)} noSearch subHeader={false} />}
      </CardBody>
    </Card>
  );
});
